import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import { PhotoAdjustDialogLayout } from '../PhotoAdjustDialogLayout/PhotoAdjustDialogLayout';
import { PhotoCropDialogProps, PhotoCropDialogRootProps, PhotoCropperProps } from './PhotoCropDialog.types';
import { useTranslation } from '@shared/core';
import { SkeletonThumbnail } from '../../../Skeleton';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

export const PhotoCropper: FC<PhotoCropperProps> = props => {
  const { imageData, aspectRatio, cropData, setCropData, maxHeight = 500, label } = props;

  const [isLoadingLatestImage, setIsLoadingLatestImage] = useState(true);

  const previousImageURL = useRef(imageData.url);

  const { tq } = useTranslation('multiPhotoSelector');

  // Set loading to true when the image URL changes
  useEffect(() => {
    if (previousImageURL.current !== imageData.url) {
      setIsLoadingLatestImage(true);
      previousImageURL.current = imageData.url;
    }
  }, [imageData.url]);

  const handleOnCrop = useCallback(
    (_, percentCrop: PercentCrop) => {
      setCropData(percentCrop);
    },
    [setCropData],
  );

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap={6}
    >
      <ReactCrop
        key={imageData.url}
        crop={cropData}
        aspect={aspectRatio}
        minHeight={100}
        minWidth={100}
        keepSelection
        style={{
          display: isLoadingLatestImage ? 'none' : 'inline-block',
        }}
        onChange={handleOnCrop}
      >
        <Box
          as="img"
          src={imageData.url}
          style={{ maxHeight: pxToRem(maxHeight) }}
          onLoad={() => setIsLoadingLatestImage(false)}
        />
      </ReactCrop>
      {/* for showing image skeleton */}
      <Box
        display={isLoadingLatestImage ? 'block' : 'none'}
        position="relative"
        width="100%"
        maxWidth={imageData.width}
        margin="0 auto"
      >
        <Box
          position="relative"
          width="auto"
          height="100%"
          aspectRatio={`${imageData.width} / ${imageData.height}`}
          maxHeight={pxToRem(maxHeight)}
          margin="0 auto"
        >
          <SkeletonThumbnail
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
          />
        </Box>
      </Box>

      <TextV2
        typographyVariant="label2"
        textAlign="center"
        color="mono10"
      >
        {label ?? tq`Pick the part of the image that you'd like to show on your site.`}
      </TextV2>
    </Flex>
  );
};

PhotoCropper.displayName = 'PhotoCropper';

const Root: FC<PhotoCropDialogRootProps> = props => {
  const { imageData, aspectRatio, crop, onClose, handleSave } = props;

  const [cropData, setCropData] = useState<Crop>(
    () =>
      crop ||
      centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspectRatio || 1,
          imageData.width,
          imageData.height,
        ),
        imageData.width,
        imageData.height,
      ),
  );

  const { tq } = useTranslation('multiPhotoSelector');

  return (
    <>
      <SpacingStack
        spacing={7}
        width="100%"
      >
        <PhotoAdjustDialogLayout.Title
          title={tq`Crop Photo`}
          onClose={onClose}
        />
        <PhotoCropper
          imageData={imageData}
          aspectRatio={aspectRatio}
          cropData={cropData}
          setCropData={setCropData}
        />
      </SpacingStack>
      <PhotoAdjustDialogLayout.ActionButtons
        handleSave={() => handleSave(cropData)}
        onClose={onClose}
      />
    </>
  );
};

export const PhotoCropDialog: FC<PhotoCropDialogProps> = props => {
  const { isOpen, onClose, imageData } = props;

  return (
    <PhotoAdjustDialogLayout
      isOpen={isOpen}
      onClose={onClose}
    >
      {imageData && (
        <Root
          {...props}
          imageData={imageData}
        />
      )}
    </PhotoAdjustDialogLayout>
  );
};

PhotoCropDialog.displayName = 'PhotoCropDialog';
